import React from "react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import type {
  ContactChannel,
  ContactCounterpart,
  ContactDetail,
  ContactOutcome,
  ContactPhoneLine,
  ContactResult,
  DunningCaseFacts,
  DunningCaseFactsPromise,
  DunningCaseStatus,
  DunningPaymentPromiseOutcome,
  ManualCloseReasonCode,
} from "../types"

dayjs.extend(utc)

/** サーバの DunningContactPolicy::DAILY_CONTACT_LIMIT と揃える */
export const DAILY_CONTACT_LIMIT = 3

/* ---------- 共通フォーマッタ ---------- */

export function formatDateTime(value: string | null | undefined) {
  if (!value) return "—"
  return dayjs(value).format("YYYY/MM/DD HH:mm")
}

export function formatDate(value: string | null | undefined) {
  if (!value) return "—"
  return dayjs(value).format("YYYY/MM/DD")
}

/** ユーザー通知へ貼り付ける督促画面の日付表記。 */
export function formatJapaneseDate(value: string | null | undefined) {
  if (!value) return "—"
  return dayjs(value).format("YYYY年MM月DD日")
}

/** ユーザー通知へ貼り付ける督促画面の日時表記。 */
export function formatJapaneseDateTime(value: string | null | undefined) {
  if (!value) return "—"
  return dayjs(value).format("YYYY年MM月DD日 HH:mm")
}

export function formatYen(value: number | null | undefined) {
  if (value == null) return "—"
  return `${value.toLocaleString("ja-JP")}円`
}

/* ---------- 共通パネル ---------- */

export const Panel: React.FC<{
  title: React.ReactNode
  headerRight?: React.ReactNode
  children: React.ReactNode
  className?: string
}> = ({ title, headerRight, children, className = "" }) => (
  <div className={`rounded-xl border border-gray-200 bg-white p-6 shadow-sm ${className}`}>
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <h3 className="flex items-center gap-2 text-[1.3rem] font-bold text-black">
        <span className="h-[14px] w-[4px] rounded-sm bg-primary" aria-hidden="true" />
        {title}
      </h3>
      {headerRight}
    </div>
    {children}
  </div>
)

/* ---------- バッジ ---------- */

/** 氏名横に常時表示するアクティブ契約数バッジ */
export const ContractCountBadge: React.FC<{ count: number | undefined; large?: boolean }> = ({
  count,
  large = false,
}) => {
  if (count == null) return null
  const emphasis =
    count >= 2
      ? "bg-blue-100 text-blue-700 border-blue-300"
      : "bg-blue-50 text-blue-700 border-blue-100"
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border font-bold tabular-nums ${emphasis} ${
        large ? "px-4 py-1 text-[1.3rem]" : "px-3 py-0.5 text-[1.1rem]"
      }`}
      title="アクティブ契約数"
    >
      契約{count}件
    </span>
  )
}

/** 要返信（ユーザー返信が未対応）バッジ */
export const AwaitingReplyBadge: React.FC<{ large?: boolean }> = ({ large = false }) => (
  <span
    className={`inline-flex shrink-0 items-center rounded-full border border-red-300 bg-red-100 font-semibold text-red-700 ${
      large ? "px-4 py-1 text-[1.2rem]" : "px-2.5 py-0.5 text-[1.1rem]"
    }`}
  >
    要返信
  </span>
)

/** 延滞日数に応じた強調 */
export function overdueDaysClass(days: number | null | undefined) {
  if (days == null) return "text-black"
  if (days >= 60) return "text-red-600"
  if (days >= 30) return "text-orange-500"
  return "text-black"
}

/** ステータスバッジの単一の情報源。一覧の状態列・詳細のチップ双方がここを参照する */
export const STATUS_CONFIG: Record<DunningCaseStatus, { label: string; className: string }> = {
  open: {
    label: "遅延中",
    className: "bg-red-100 text-red-700 border border-red-300",
  },
  closed: {
    label: "クローズ",
    className: "bg-green-100 text-green-700 border border-green-300",
  },
  paused: {
    label: "保留",
    className: "bg-amber-100 text-amber-800 border border-amber-300",
  },
  resolved: {
    label: "遅延解消",
    className: "bg-blue-100 text-blue-700 border border-blue-300",
  },
  monitoring: {
    label: "遅延前",
    className: "bg-violet-100 text-violet-700 border border-violet-300",
  },
}

/**
 * 約束日時がまだ有効か。23:59 の約束はその日の 23:59:59 まで有効、それ以外（15:00 など）は約束時刻ちょうどまで。
 * サーバの DunningPaymentPromise.effective_deadline_at と同じ規則（時刻は JST で見る）。
 */
export function isPromiseScheduleActive(scheduled: dayjs.Dayjs, now: dayjs.Dayjs = dayjs()) {
  const jst = scheduled.utcOffset(9 * 60)
  const deadline = jst.hour() === 23 && jst.minute() === 59 ? jst.endOf("day") : scheduled
  return !deadline.isBefore(now)
}

/**
 * 今も有効な支払約束があるか（表示用）。
 * facts.promise.active はスナップショットで、約束日時が過ぎても次の再計算（9:30/13:00/15:30/19:30）まで
 * true のまま残る。約束日時が分かるときは現在時刻でも判定し、期限切れの「約束中」を出さない。
 */
export function isPromiseActiveNow(
  promise: DunningCaseFactsPromise | null | undefined,
  now: dayjs.Dayjs = dayjs()
) {
  if (promise?.active !== true) return false
  const scheduled = promise.scheduledOn ? dayjs(promise.scheduledOn) : null
  return scheduled?.isValid() ? isPromiseScheduleActive(scheduled, now) : true
}

export type PromiseChipConfig = { label: string; className: string }

export const NEUTRAL_CHIP_CLASS = "bg-gray-100 text-black"

/** 有効な約束の結果（API の outcome）のラベルと色。約束パネルのチップと、遅延状況の欄の最新約束の結果で使う */
export const PROMISE_OUTCOME_CONFIG: Record<DunningPaymentPromiseOutcome, PromiseChipConfig> = {
  pending: { label: "約束中", className: "bg-green-100 text-green-700" },
  fulfilled: { label: "履行", className: "bg-blue-100 text-blue-700" },
  broken: { label: "不履行", className: "bg-red-100 text-red-700" },
  unknown: { label: "判定不可", className: NEUTRAL_CHIP_CLASS },
}

/**
 * 状態ラベルの横に出す約束の表示。枠線の有無（`border`）は描画する側で付ける。
 * - active: 有効な約束がある（約束中）
 * - none: 有効な約束が無い（約束なし）。約束の有無で後ろのラベルの位置がずれないよう常に何かを出すためのもので、
 *   情報としての重みは低いのでアウトラインのグレーで控えめにする
 */
export const PROMISE_CHIPS = {
  active: { label: "約束中", className: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  none: { label: "約束なし", className: "bg-white text-gray-500 border-gray-300" },
} as const

export type PromiseChipState = keyof typeof PROMISE_CHIPS

/**
 * 状態ラベルの横に出す約束の表示を決める。約束の有無が分からないときは undefined（何も出さない）。
 * - facts が無い（未集計）
 * - 再計算に失敗している。初回の失敗では約束の情報が空の既定値になるため、「約束なし」と断定しない
 *   （財務訂正による再集計待ち＝stale は直前の集計の約束情報を持っているので判定に使う）
 * 約束中かどうかは facts.promise.active のスナップショットでなく約束日時で判定する（isPromiseActiveNow）
 */
export function promiseChipState(
  facts: Pick<DunningCaseFacts, "promise" | "refreshStatus"> | null | undefined,
  now: dayjs.Dayjs = dayjs()
): PromiseChipState | undefined {
  if (isPromiseActiveNow(facts?.promise, now)) return "active"
  if (facts?.promise == null || facts.refreshStatus === "failed") return undefined
  return "none"
}

export const CaseStatusChip: React.FC<{
  /** 既知ステータスは補完・タイポ検出を効かせつつ、サーバ先行デプロイの未知値も受ける */
  status: DunningCaseStatus | (string & {})
  /**
   * 状態とは独立した約束の表示（promiseChipState の値）。
   * 約束の情報を持たない呼び出し元（未指定）では何も出さない
   */
  promise?: PromiseChipState
}> = ({ status, promise }) => {
  const cfg = STATUS_CONFIG[status as DunningCaseStatus] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border border-gray-300",
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span
        className={`inline-block rounded-full px-2.5 py-0.5 text-[1.1rem] font-medium ${cfg.className}`}
      >
        {cfg.label}
      </span>
      {promise && (
        <span
          className={`inline-block rounded-full border px-2.5 py-0.5 text-[1.1rem] font-medium ${PROMISE_CHIPS[promise].className}`}
        >
          {PROMISE_CHIPS[promise].label}
        </span>
      )}
    </span>
  )
}

export const OPEN_REASON_LABELS: Record<string, string> = {
  delinquency_compile: "遅延コンパイル",
  manual: "手動",
}

export const CLOSE_REASON_LABELS: Record<string, string> = {
  completed: "完済",
  history_import: "履歴補完",
  // Legacy code kept so historical cases closed before the rename still resolve a label.
  no_longer_delinquent: "遅延解消",
  guaranteed_contract: "保証履行",
  debt_consolidated_contract: "債務整理",
  deceased: "死亡",
  inheritance_renounced: "相続放棄",
  discharged: "免責",
  other: "その他",
  monitoring_ended: "ウォッチ終了",
  manual: "手動",
}

export const PAUSE_REASON_OPTIONS = [
  "債務整理",
  "回収委託",
  "支払停止の抗弁",
  "死亡懸念",
  "譲渡系",
  "その他",
] as const

/** Operator-selectable close reasons (order shown in the close dialog). */
export const MANUAL_CLOSE_REASON_OPTIONS: { code: ManualCloseReasonCode; label: string }[] = [
  { code: "guaranteed_contract", label: "保証履行" },
  { code: "inheritance_renounced", label: "相続放棄" },
  { code: "discharged", label: "免責" },
  { code: "other", label: "その他" },
]

/** monitoring ケースの終了理由。「ウォッチ終了」は再延滞時の自動再開を止めない。 */
export const MONITORING_CLOSE_REASON_OPTIONS: { code: ManualCloseReasonCode; label: string }[] = [
  { code: "monitoring_ended", label: "ウォッチ終了" },
  ...MANUAL_CLOSE_REASON_OPTIONS,
]

/** APIの監査イベント種別 */
export const AUDIT_EVENT_LABELS: Record<string, string> = {
  case_opened: "ケース開設",
  case_paused: "ケース停止",
  case_resumed: "ケース再開",
  case_closed: "ケース終了",
  case_reopened: "ケース再開",
  case_resolved: "遅延解消",
  case_monitoring_started: "ウォッチ開始",
  case_updated: "ケース更新",
  operation_flags_updated: "連絡可否設定更新",
  contract_attached: "契約リンク追加",
  contract_voided: "契約リンク無効化",
  contract_deleted: "契約リンク削除",
  context_compiled: "詳細更新",
  context_compile_failed: "詳細更新失敗",
  contact_logged: "連絡記録",
  contact_log_updated: "連絡記録の編集",
  contact_log_deleted: "連絡記録の削除",
  draft_generated: "AIドラフト生成",
  draft_discarded: "AIドラフト破棄",
  draft_sent: "AIドラフト送信",
  auto_sent: "AI自動送信",
  promise_registered: "支払約束登録",
  promise_canceled: "支払約束取消",
  promise_ended: "支払約束の終了（前回の延滞）",
  chat_room_status_updated: "チャットステータス更新",
  awaiting_reply_completed: "返信不要として完了",
  awaiting_reply_reopened: "要返信に戻す",
  awaiting_reply_chat_status_cleared: "チャットステータス更新（返信不要完了に連動）",
  awaiting_reply_chat_status_restored: "チャットステータス更新（要返信に戻すのに連動）",
  chat_room_status_synced_on_delinquency: "チャットステータス更新（延滞の取り込みに連動）",
}

/** APIの監査実行者種別 */
export const AUDIT_ACTOR_LABELS: Record<string, string> = {
  Smoother: "オペレーター",
  System: "システム",
}

/** APIの連絡手段 */
export const CONTACT_CHANNEL_LABELS: Record<string, string> = {
  phone: "電話",
  sms: "SMS",
  email: "メール",
  line_manual: "LINE（手動）",
  line_auto: "LINE（自動送信）",
  line_bulk: "LINE（一括送信）",
  liff: "督促リフ",
  in_person: "対面",
  other: "その他",
}

/** APIの連絡方向 */
export const CONTACT_DIRECTION_LABELS: Record<string, string> = {
  outbound: "発信",
  inbound: "受信",
}

/** APIの連絡結果 */
export const CONTACT_OUTCOME_LABELS: Record<string, string> = {
  reached: "通話成立",
  no_answer: "不在",
  left_voicemail: "留守電",
  promised_payment: "支払約束",
  refused: "拒否",
  wrong_contact: "連絡先誤り",
  other: "その他",
}

/**
 * Channel buttons in the record-contact dialog (order shown).
 * システム自動記録チャンネル（line_bulk / liff）は手動入力の対象外なので含めない。
 */
export const CONTACT_CHANNEL_OPTIONS: { code: ContactChannel; label: string }[] = [
  { code: "phone", label: "電話" },
  { code: "sms", label: "SMS" },
  { code: "email", label: "メール" },
  { code: "other", label: "その他" },
]

/** システムが自動記録するチャンネルか（編集モーダルでの書き換えを禁止する） */
export const isSystemContactChannel = (channel: string): boolean =>
  !CONTACT_CHANNEL_OPTIONS.some((opt) => opt.code === channel)

/** Outcome buttons in the record-contact dialog (optional). */
export const CONTACT_OUTCOME_OPTIONS: { code: ContactOutcome; label: string }[] = [
  { code: "reached", label: "通話成立" },
  { code: "no_answer", label: "不在" },
  { code: "left_voicemail", label: "留守電" },
  { code: "promised_payment", label: "支払約束" },
  { code: "refused", label: "拒否" },
  { code: "wrong_contact", label: "連絡先誤り" },
  { code: "other", label: "その他" },
]

/* ---------- 交渉記録フィールド ---------- */

/** 発信に使った回線 */
export const CONTACT_PHONE_LINE_LABELS: Record<string, string> = {
  dunning_fixed: "督促固定",
  cs_fixed: "CS固定",
  mobile_1: "1号携帯",
  mobile_2: "2号携帯",
}

export const CONTACT_PHONE_LINE_OPTIONS: { code: ContactPhoneLine; label: string }[] = [
  { code: "dunning_fixed", label: "督促固定" },
  { code: "cs_fixed", label: "CS固定" },
  { code: "mobile_1", label: "1号携帯" },
  { code: "mobile_2", label: "2号携帯" },
]

/** 連絡相手 */
export const CONTACT_COUNTERPART_LABELS: Record<string, string> = {
  self: "本人携帯",
  workplace: "勤務先",
  emergency_contact: "緊急連絡先",
  other: "その他",
}

export const CONTACT_COUNTERPART_OPTIONS: { code: ContactCounterpart; label: string }[] = [
  { code: "self", label: "本人携帯" },
  { code: "workplace", label: "勤務先" },
  { code: "emergency_contact", label: "緊急連絡先" },
  { code: "other", label: "その他" },
]

/** 接触結果 */
export const CONTACT_RESULT_LABELS: Record<string, string> = {
  success: "成功",
  failed: "失敗",
  one_way: "一方的送信",
}

export const CONTACT_RESULT_OPTIONS: { code: ContactResult; label: string }[] = [
  { code: "success", label: "成功" },
  { code: "failed", label: "失敗" },
  { code: "one_way", label: "一方的送信" },
]

/** 電話の内容詳細 */
export const CONTACT_DETAIL_LABELS: Record<string, string> = {
  voicemail_left: "留守電吹き込み",
  call_only: "コールのみ",
  call_then_voicemail: "コール後留守電",
  call_then_guidance: "コール後ガイダンス",
  hangup_during_ring: "コール中先方切電",
  hangup_after_connect: "通電後先方切電",
  busy_tone: "話中音",
  power_off: "電源オフ",
  not_in_use: "使用なし",
  stopped_by_user: "都合ストップ",
}

export const CONTACT_DETAIL_OPTIONS: { code: ContactDetail; label: string }[] = (
  Object.entries(CONTACT_DETAIL_LABELS) as [ContactDetail, string][]
).map(([code, label]) => ({ code, label }))

/** 発信でこの内容を選んだとき、既定で連絡回数に数えない内容 */
export const NON_COUNTING_CONTACT_DETAILS: readonly ContactDetail[] = [
  "not_in_use",
  "stopped_by_user",
]
